import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { Partner } from '../interfaces/partner';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PartnerService } from '../services/partner.service';
import {CommonModule, formatDate} from '@angular/common';
import {RouterLink} from "@angular/router";
import {TeacherService} from "../services/teacher.service";
import {Teacher} from "../interfaces/teacher";


@Component({
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, RouterLink],
  selector: 'app-partner-form',
  templateUrl: './partner-form.component.html',
  styleUrl: './partner-form.component.css'
})
export class PartnerFormComponent {
  @Input() partner: Partner | null = null;
  @Output() formSubmitted = new EventEmitter<any>();

  partnerService = inject(PartnerService)
  partnerForm!: FormGroup;
  teacher: Teacher | null = null;
  async ngOnInit() {
    this.teacher = await this.teacherService.getTeacher();
    console.log('PartnerSubmissionForm component initialized', this.partner);
    this.initializeForm();
  }

  servicesOffered = [
    'Email/Newsletter', 'Lesson Plans', 'Lesson Supplies', 'Technical Support',
    'Speaker/Volunteers', 'Virtual Field Trip', 'In-Person Field Trip',
    'Service Project', 'Internships', 'Scholarships'
  ];

  focusAreas = [
    'Clean Air', 'Clean Water', 'Food Waste', 'Composting', 'Recycling',
    'Habitats', 'Pollinators'
  ];

  fundingSupport = [
    'Foundational Gift', 'Grant RFP', 'In Kind support', 'Sponsorship'
  ];

  feeServices = [
    'Govt entity', 'Corp', 'not-for-profit', 'Academia', 'Other'
  ];

  initializeForm() {
    this.partnerForm = this.fb.group({
      id: [this.partner?.id || null],
      org: [this.partner?.org || '', Validators.required],
      primeContact: [this.partner?.primeContact || '', Validators.required],
      contactEmail: [this.partner?.contactEmail || '', [Validators.required, Validators.email]],
      contactPhone: [this.partner?.contactPhone || '', [Validators.required, Validators.pattern('[0-9]{10}')]],
      areaOfService: [this.partner?.areaOfService || '', Validators.required],
      website: [this.partner?.website || '', Validators.required],
      zipcodesServed: [this.partner?.zipcodesServed || ''],
      studentCapacity: [this.partner?.studentCapacity || '', [Validators.required, Validators.min(1)]],
      bio: [this.partner?.bio || '', Validators.required],
      titleOneDiscount: [this.partner?.titleOneDiscount || false],
      notes: [this.partner?.notes || ''],
      servicesOffered: this.fb.array(
        this.servicesOffered.map((service) => this.fb.control(this.partner?.servicesOffered?.includes(service) || false))
      ),
      focusAreas: this.fb.array(
        this.focusAreas.map((focus) => this.fb.control(this.partner?.focusAreas?.includes(focus) || false))
      ),
      fundingSupport: this.fb.array(
        this.fundingSupport.map((focus) => this.fb.control(this.partner?.fundingSupport?.includes(focus) || false))
      ),
      feeType: [this.partner?.feeType || '', Validators.required],
      feeServices: this.fb.array(
        this.feeServices.map((focus) => this.fb.control(this.partner?.feeServices?.includes(focus) || false))
      ),
      referredBy: [this.partner?.referredBy || ''],
      status: [this.partner?.status || '', Validators.required],
    });
  }

  constructor(private fb: FormBuilder, private teacherService: TeacherService) {}

  get servicesOfferedControls(): FormControl[] {
    return (this.partnerForm.get('servicesOffered') as FormArray).controls as FormControl[];
  }

  get focusAreasControls(): FormControl[] {
    return (this.partnerForm.get('focusAreas') as FormArray).controls as FormControl[];
  }

  get fundingSupportControls(): FormControl[] {
    return (this.partnerForm.get('fundingSupport') as FormArray).controls as FormControl[];
  }

  get feeServicesControls(): FormControl[] {
    return (this.partnerForm.get('feeServices') as FormArray).controls as FormControl[];
  }

  focusAreaDataConvert() {
    const focusAreasArray = this.partnerForm.value.focusAreas ?? [];
    return this.focusAreas
      .filter((_, index) => focusAreasArray[index])
      .map(area => area);
  }

  servicesOfferedDataConverter() {
    const servicesArray = this.partnerForm.value.servicesOffered ?? [];
    return this.servicesOffered
      .filter((_, index) => servicesArray[index])
      .map(service => service);
  }

  submitPartner(event: Event) {
    event.preventDefault();

    if (!this.partnerForm.valid) {
      this.partnerForm.markAllAsTouched();
      return;
    }

    const focusAreas = this.focusAreaDataConvert();
    const servicesOffered = this.servicesOfferedDataConverter();
    const teacherFullName = this.teacher ? `${this.teacher.fName} ${this.teacher.lName}` : '';

    const partnerData: Partner = {
      id: this.partnerForm.value.id ?? null,
      org: this.partnerForm.value.org ?? '',
      isDeleted: false,
      notes: this.partnerForm.value.notes ?? '',
      urlOrg: this.partnerForm.value.org.replace(/\s+/g, ''),
      primeContact: this.partnerForm.value.primeContact ?? '',
      contactEmail: this.partnerForm.value.contactEmail ?? '',
      contactPhone: this.partnerForm.value.contactPhone ?? '',
      areaOfService: this.partnerForm.value.areaOfService ?? '',
      zipcodesServed: this.partnerForm.value.zipcodesServed ?? '',
      website: this.partnerForm.value.website ?? '',
      bio: this.partnerForm.value.bio ?? '',
      studentCapacity: Number(this.partnerForm.value.studentCapacity) ?? 0,
      titleOneDiscount: Boolean(this.partnerForm.value.titleOneDiscount) ?? false,
      servicesOffered,
      focusAreas,
      fundingSupport: this.fundingSupport,
      feeType: this.partnerForm.value.feeType,
      feeServices: this.feeServices,
      referredBy: this.teacher?.fName ?? '',
      status: this.partnerForm.value.status,
      createdBy: this.partner?.createdBy || teacherFullName,
      modifiedBy: teacherFullName,
      logo: '',
      cost: 0
    };

    this.formSubmitted.emit(partnerData);
  }

  async toggleAllServices() {
    const services = document.querySelectorAll<HTMLInputElement>(".service");
    const selectAll = document.querySelector<HTMLInputElement>("#selectAllServices");
    const isChecked = selectAll?.checked ?? false;

    services.forEach(service => {
      if (service !== selectAll) {
        service.checked = isChecked;
        service.dispatchEvent(new Event("change"));
      }
    });
  }

  async toggleAllFAs() {
    const services = document.querySelectorAll<HTMLInputElement>(".fa");
    const selectAll = document.querySelector<HTMLInputElement>("#selectAllFAs");
    const isChecked = selectAll?.checked ?? false;

    services.forEach(service => {
      if (service !== selectAll) {
        service.checked = isChecked;
        service.dispatchEvent(new Event("change"));
      }
    });

  }

  allowNumbersOnly(event: KeyboardEvent) {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
    if (allowedKeys.includes(event.key)) return;
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  protected readonly formatDate = formatDate;
}
